import React from 'react';
import {
  Box,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  alpha,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChecklistStep } from './types';
import { CompletedCheckIcon } from './AnimatedCheckIcon';
import { slideDown, TIMING, EASING, BLURPLE } from './animations';

interface ActivationChecklistItemProps {
  step: ChecklistStep;
  isCompleted: boolean;
  isCurrent: boolean;
  onClick: () => void;
  /** Step number (1-based) for pending items */
  stepNumber: number;
  /** Animation delay for staggered entrance (ms) */
  animationDelay?: number;
}

/**
 * Individual checklist item showing step title, description, and completion state
 *
 * Enhanced with:
 * - Left accent bar on current step (BLURPLE)
 * - Step numbers for pending items (instead of empty circles)
 * - Staggered entrance animation
 * - Muted styling for completed items
 */
export function ActivationChecklistItem({
  step,
  isCompleted,
  isCurrent,
  onClick,
  stepNumber,
  animationDelay = 0,
}: ActivationChecklistItemProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <ListItem
      disablePadding
      sx={{
        // Staggered entrance animation
        animation: `${slideDown} ${TIMING.normal} ${EASING.decelerate} forwards`,
        animationDelay: `${animationDelay}ms`,
        opacity: 0, // Start invisible for animation
        animationFillMode: 'forwards',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          opacity: 1,
        },
      }}
    >
      <ListItemButton
        onClick={onClick}
        disabled={isCompleted}
        sx={{
          position: 'relative',
          py: 1,
          px: 1.5,
          borderRadius: 1,
          // Muted appearance for completed items
          opacity: isCompleted ? 0.6 : 1,
          // Current step: subtle highlight with left accent bar
          ...(isCurrent && {
            bgcolor: alpha(BLURPLE, theme.palette.mode === 'dark' ? 0.15 : 0.08),
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: 3,
              borderRadius: 1.5,
              bgcolor: BLURPLE,
            },
          }),
          '&:hover': {
            bgcolor: isCompleted
              ? 'transparent'
              : isCurrent
                ? alpha(BLURPLE, theme.palette.mode === 'dark' ? 0.2 : 0.12)
                : 'action.hover',
          },
          cursor: isCompleted ? 'default' : 'pointer',
          transition: `background-color ${TIMING.fast} ${EASING.smooth}`,
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          {isCompleted ? (
            <CompletedCheckIcon size={20} />
          ) : (
            // Step number badge for pending items
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: isCurrent ? BLURPLE : alpha(theme.palette.text.primary, 0.3),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: isCurrent ? BLURPLE : alpha(theme.palette.text.primary, 0.5),
                transition: `all ${TIMING.fast} ${EASING.smooth}`,
              }}
            >
              {stepNumber}
            </Box>
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: isCurrent ? 600 : 400,
                color: isCompleted
                  ? 'text.disabled'
                  : isCurrent
                    ? theme.palette.mode === 'dark' ? 'text.primary' : 'text.primary'
                    : 'text.secondary',
                textDecoration: isCompleted ? 'line-through' : 'none',
                transition: `color ${TIMING.fast} ${EASING.smooth}`,
              }}
            >
              {t(step.titleKey)}
            </Typography>
          }
          secondary={
            !isCompleted && isCurrent ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25 }}
              >
                {t(step.descriptionKey)}
              </Typography>
            ) : null
          }
        />
      </ListItemButton>
    </ListItem>
  );
}
